import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { CloudflareAgentCoordinatorRpcError } from "schemas/errors";

/**
 * Determines whether a failed request should be retried. Returns false for 401/403 errors
 * since these are authentication/authorization failures that won't be resolved by retrying.
 *
 * @author Noah 2026-01-14
 */
function shouldRetryRequest(failureCount: number, error: unknown): boolean {
  // Note (Noah, 2026-01-14): Don't retry on 401/403 errors - these are auth failures
  // that won't be resolved by retrying and can cause infinite loops
  if (error instanceof TRPCClientError) {
    const status = error.data?.httpStatus ?? error.shape?.data?.httpStatus;
    if (status === 401 || status === 403) {
      return false;
    }
  }
  // Default React Query behavior: retry up to 3 times
  return failureCount < 3;
}

/**
 * Client used for all queries, and gives us some sane default options. We set `staleTime` to
 * infinity so we only only refetch when we need to by default - we can override when we need to.
 */
function createReploQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Number.POSITIVE_INFINITY,
        retry: shouldRetryRequest,
      },
    },
    mutationCache: new MutationCache({
      onError: (error, _, __, mutation) => {
        if (mutation.meta?.reploIgnoreDefaultErrorHandling) {
          return;
        }
        // NOTE (Max, 2026-04-21): DO RPC errors are handled via the
        // "agent-coordinator-error" WebSocket broadcast from the DO, which
        // carries the server-side errorId and userFacingDetails. We suppress
        // them here to avoid double-toasting — the broadcast handler in
        // useReploAgentConnection shows the appropriate toast based on
        // dev/superuser status.
        if (error instanceof CloudflareAgentCoordinatorRpcError) {
          return;
        }
        // Note (Noah, 2025-10-03): We have to dynamically import this to avoid a circular dependency,
        // since error handling needs to import TRPC routers (which we're currently constructing)
        void import("@v2/utils/error-handling").then((module) => {
          if (error instanceof TRPCClientError) {
            // For a call site that shows its own UI for some server errors and leaves the rest to this handler.
            const ignoredKeys =
              mutation.meta?.reploIgnoreDefaultErrorHandlingForKeys;
            if (
              Array.isArray(ignoredKeys) &&
              ignoredKeys.includes(
                module.resolveTRPCClientErrorDetails(error)?.errorKey,
              )
            ) {
              return;
            }
            module.handleTRPCClientError(error);
          } else {
            module.handleNonTRPCClientError(error);
          }
        });
      },
    }),
    // Note (Noah, 2024-05-27): This is how you define a global error handler in TRPC, see
    // https://tkdodo.eu/blog/react-query-error-handling#the-global-callbacks
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.reploIgnoreDefaultErrorHandling) {
          return;
        }
        // NOTE (Max, 2026-04-21): Same as MutationCache — DO errors are
        // handled via the "agent-coordinator-error" broadcast.
        if (error instanceof CloudflareAgentCoordinatorRpcError) {
          return;
        }
        void import("@v2/utils/error-handling").then((module) => {
          if (error instanceof TRPCClientError) {
            module.handleTRPCClientError(error);
          } else {
            module.handleNonTRPCClientError(error);
          }
        });
      },
    }),
  });
}

declare global {
  // Note (Noah, 2025-10-03): We save these on the window instead of in a global variable here to
  // avoid "used before assignment" errors
  interface Window {
    _reploNamespacedQueryClients?: Map<string, QueryClient>;
    _reploQueryClient?: QueryClient;
  }
}

export function getNamespacedQueryClient(namespace = "default"): QueryClient {
  let namespacedQueryClients = window._reploNamespacedQueryClients;
  if (!namespacedQueryClients) {
    window._reploNamespacedQueryClients = new Map<string, QueryClient>();
    namespacedQueryClients = window._reploNamespacedQueryClients;
  }
  const existing = namespacedQueryClients.get(namespace);
  if (existing) {
    return existing;
  }
  const client = createReploQueryClient();
  namespacedQueryClients.set(namespace, client);
  return client;
}

export function getQueryClient(): QueryClient {
  if (window._reploQueryClient) {
    return window._reploQueryClient;
  }
  window._reploQueryClient = createReploQueryClient();
  return window._reploQueryClient;
}
